import React, { useState, useEffect } from 'react';
import Header from '../Header';
import { vendorAPI } from '../../services/api';
import { notify, confirm } from '../../utils/notify';
import { useListControls, ListToolbar, Pagination } from '../../components/ListControls';
import Topbar from "../../components/Topbar";

const money = n => new Intl.NumberFormat('en-IN',{style:'currency',currency:'INR',maximumFractionDigits:0}).format(Number(n)||0);
const statusCls = { PENDING_FINANCE:'status-pending', FINANCE_APPROVED:'status-inreview', PAYOUT_INITIATED:'status-inreview', PAID:'status-approved', ON_HOLD:'status-hold' };

function FinanceProcessing() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);

  const load = () => {
    setLoading(true);
    vendorAPI.listFinanceQueue({ status: 'ALL', limit: 200 }).then(d => setRequests(d.requests||[])).catch(() => notify.error('Failed to load')).finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const handleProcess = async (r) => {
    const res = await confirm.financeProcess(money(r.processedAmount || r.requestedAmount));
    if (!res.isConfirmed) return;
    setActing(r._id);
    try {
      await vendorAPI.processPayment(r._id, { paymentType: res.value.payType, processedAmount: res.value.partialAmt, holdReason: res.value.holdReason });
      notify.success(`Payment ${res.value.payType === 'HOLD' ? 'put on hold' : 'processed'}!`);
      await load();
    } catch (e) { notify.error(e.message || 'Failed'); }
    finally { setActing(null); }
  };

  // Finance-level approval (after L1 → L2 → L3). Also used to re-approve a
  // request that was put on hold.
  const handleFinanceApprove = async (r) => {
    const res = await confirm.action(`Finance-approve payment for ${r.vendorId?.name || 'this vendor'}?`, `Amount: ${money(r.requestedAmount)}`, 'Yes, Approve', '#16a34a');
    if (!res.isConfirmed) return;
    setActing(r._id);
    try { await vendorAPI.approvePaymentRequest(r._id, { remarks: 'Finance approved' }); notify.success('Finance approved — ready for processing'); await load(); }
    catch (e) { notify.error(e.message || 'Failed'); }
    finally { setActing(null); }
  };
  const handleFinanceReject = async (r) => {
    const res = await confirm.reject(`Reject payment request for ${r.vendorId?.name || 'this vendor'}?`);
    if (!res.isConfirmed) return;
    setActing(r._id);
    try { await vendorAPI.rejectPaymentRequest(r._id, { remarks: res.value }); notify.success('Request rejected'); await load(); }
    catch (e) { notify.error(e.message || 'Failed'); }
    finally { setActing(null); }
  };

  const handlePayout = async (r) => {
    const res = await confirm.action('Initiate Razorpay Payout?', `Vendor: ${r.vendorId?.name} | Amount: ${money(r.processedAmount||r.requestedAmount)}`, 'Yes, Release Payout', '#1a5276');
    if (!res.isConfirmed) return;
    setActing(r._id);
    try { await vendorAPI.payout(r._id); notify.success('Razorpay payout initiated! UTR generated.'); await load(); }
    catch (e) { notify.error(e.message || 'Failed'); }
    finally { setActing(null); }
  };

  const lc = useListControls(requests, {
    searchFields: [r=>r.vendorId?.name, 'paymentRequestCode', r=>r.vendorId?.vendorCode],
    dateField: 'createdAt', pageSize: 10,
  });

  return (
    <>
      <Header/>
      <div id="db-wrapper"><main id="page-content">
          <Topbar section="Vendor Payment" /><section className="container-fluid p-4 pt-3">
        <div className="page-hero"><div><h5 className="text-white lh-1">Finance Processing</h5><div className="sub">Verify and process payments (Full / Partial / Hold) then initiate Razorpay payout</div></div></div>

        <div className="row g-2 mb-3">
          {[{l:'Pending Finance',v:requests.filter(r=>['PENDING_FINANCE','FINANCE_APPROVED'].includes(r.status)).length,bg:'#fffbeb',ic:'#f59e0b'},{l:'Payout Initiated',v:requests.filter(r=>r.status==='PAYOUT_INITIATED').length,bg:'#eff6ff',ic:'#3b82f6'},{l:'Paid',v:requests.filter(r=>r.status==='PAID').length,bg:'#f0fdf4',ic:'#22c55e'},{l:'On Hold',v:requests.filter(r=>r.status==='ON_HOLD').length,bg:'#fef2f2',ic:'#ef4444'}].map((s,i)=>(
            <div key={i} className="col-6 col-md-3"><div className="kpi-card d-flex align-items-center gap-2"><div className="kpi-icon" style={{background:s.bg}}><i className="bi bi-bank" style={{color:s.ic}}/></div><div><div style={{fontSize:'1.17rem',fontWeight:700}}>{s.v}</div><div style={{fontSize:'0.7rem',color:'#6b7280'}}>{s.l}</div></div></div></div>
          ))}
        </div>

        {loading ? <div className="text-center py-5"><span className="spinner-border text-primary"/></div>
        : requests.length===0 ? <div className="empty-state"><i className="bi bi-bank"/><p>No finance queue items</p></div>
        : (<>
        <ListToolbar search={lc.search} setSearch={lc.setSearch} dateFrom={lc.dateFrom} setDateFrom={lc.setDateFrom} dateTo={lc.dateTo} setDateTo={lc.setDateTo} placeholder="Search by vendor, code..." dateLabel="Submitted"/>
        {lc.paginated.map((r,idx) => (
          <div key={r._id} className="record-card">
            <div className="record-number">{(lc.page-1)*lc.pageSize+idx+1}.</div>
            <div className="record-body">
              <div className="d-flex align-items-center gap-2 mb-1 flex-wrap">
                <div style={{fontWeight:700,fontSize:'0.874rem',color:'#111827'}}>{r.vendorId?.name||'—'}</div>
                <span className={`status-pill ${statusCls[r.status]||'status-pending'}`}>{r.status?.replace(/_/g,' ')}</span>
                <span style={{background:'#f0fdf4',color:'#16a34a',fontSize:'0.7rem',fontWeight:600,padding:'2px 6px',borderRadius:12}}>{money(r.processedAmount||r.requestedAmount)}</span>
              </div>
              <div className="row g-2">
                <div className="col-12 col-md-4">
                  <div className="record-field"><span className="rf-label">Code — </span><span className="rf-value text-primary">{r.paymentRequestCode}</span></div>
                  <div className="record-field"><span className="rf-label">Vendor Code — </span><span className="rf-value">{r.vendorId?.vendorCode}</span></div>
                  <div className="record-field"><span className="rf-label">Project — </span><span className="rf-value">{r.projectId?.projectCode}</span></div>
                </div>
                <div className="col-12 col-md-4">
                  <div className="record-field"><span className="rf-label">Requested — </span><span className="rf-value fw-bold">{money(r.requestedAmount)}</span></div>
                  {r.grnId && <div className="record-field"><span className="rf-label">GRN Payable — </span><span className="rf-value fw-bold text-success">{money(r.grnId.payableAmount)}</span></div>}
                  {r.processedAmount && <div className="record-field"><span className="rf-label">Processed — </span><span className="rf-value fw-bold text-primary">{money(r.processedAmount)}</span></div>}
                </div>
                <div className="col-12 col-md-4">
                  <div className="record-field"><span className="rf-label">Bank — </span><span className="rf-value">{r.vendorId?.bankName||'—'}</span></div>
                  <div className="record-field"><span className="rf-label">Account — </span><span className="rf-value">{r.vendorId?.accountNumber||'—'}</span></div>
                  {r.utrNumber && <div className="record-field"><span className="rf-label">UTR — </span><span className="rf-value fw-bold text-success">{r.utrNumber}</span></div>}
                </div>
              </div>
            </div>
            <div className="divider d-none d-md-block"/>
            <div className="record-actions">
              {r.status==='PENDING_FINANCE' && (
                <>
                  {r.holdReason && <div className="small text-muted mb-1"><i className="bi bi-pause-circle me-1"/>On hold: {r.holdReason}</div>}
                  <button className="btn-card-approve" onClick={() => handleFinanceApprove(r)} disabled={acting===r._id}>
                    {acting===r._id ? <span className="spinner-border spinner-border-sm"/> : <><i className="bi bi-check-lg me-1"/>Finance Approve</>}
                  </button>
                  <button className="btn-card-reject" onClick={() => handleFinanceReject(r)} disabled={acting===r._id}>
                    <i className="bi bi-x-lg me-1"/>Reject
                  </button>
                </>
              )}
              {r.status==='FINANCE_APPROVED' && (
                <button className="btn-card-approve" onClick={() => handleProcess(r)} disabled={acting===r._id}>
                  {acting===r._id ? <span className="spinner-border spinner-border-sm"/> : <><i className="bi bi-gear me-1"/>Process Payment</>}
                </button>
              )}
              {r.status==='PAYOUT_INITIATED' && (
                <button className="btn-card-approve" onClick={() => handlePayout(r)} disabled={acting===r._id}>
                  {acting===r._id ? <span className="spinner-border spinner-border-sm"/> : <><i className="bi bi-send-check me-1"/>Release Payout</>}
                </button>
              )}
              {r.status==='PAID' && <span className="status-pill status-approved"><i className="bi bi-check-circle"/>Paid</span>}
              {r.status==='ON_HOLD' && <span className="status-pill status-hold"><i className="bi bi-pause-circle"/>On Hold</span>}
            </div>
          </div>
        ))}
        <Pagination page={lc.page} totalPages={lc.totalPages} setPage={lc.setPage} totalCount={lc.totalCount} pageSize={lc.pageSize} itemLabel="requests"/>
        </>)}
      </section></main></div>
    </>
  );
}
export default FinanceProcessing;
